<!-- src/lib/components/migration/ProjectSelector.svelte -->
<!-- Step 3: Pick a project/team and trigger the fetch -->
<script lang="ts">
  import type { MigrationSource, ProgressResponse } from '../../types.ts';
  import { api } from '../../api.ts';

  interface Props {
    source: MigrationSource;
    displayName: string;
    workspaceId: number;
    projectId: number;
    onFetched: (runId: string) => void;
    onBack: () => void;
  }
  let { source, displayName, workspaceId, projectId, onFetched, onBack }: Props = $props();

  let sourceProjectId = $state('');
  let fetching = $state(false);
  let statusText = $state<string | null>(null);
  let error = $state<string | null>(null);
  let pollTimer = $state<ReturnType<typeof setInterval> | null>(null);

  const sourceLabel: Record<MigrationSource, string> = {
    linear: 'Team ID', plane: 'Project ID', asana: 'Project GID', figma: 'File Key',
  };
  const sourcePlaceholder: Record<MigrationSource, string> = {
    linear: 'e.g. ENG', plane: 'e.g. proj-uuid', asana: '123456789', figma: 'abc123xyz',
  };

  function stopPolling() {
    if (pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  async function pollProgress(runId: string) {
    try {
      const res = await api<ProgressResponse>(`/api/migrate/progress/${runId}`);
      statusText = `Status: ${res.status}`;
      if (res.status === 'analyzing' || res.status === 'done') {
        stopPolling();
        onFetched(runId);
      } else if (res.status === 'error') {
        stopPolling();
        error = res.error ?? 'Fetch failed.';
        fetching = false;
      }
    } catch (e) {
      stopPolling();
      error = e instanceof Error ? e.message : 'Failed to poll progress.';
      fetching = false;
    }
  }

  async function fetchData() {
    if (!sourceProjectId.trim()) return;
    fetching = true;
    error = null;
    statusText = 'Starting fetch…';

    try {
      const res = await api<{ run_id: string; status: string }>('/api/migrate/fetch', {
        method: 'POST',
        body: JSON.stringify({
          source_type: source,
          source_project_id: sourceProjectId.trim(),
          workspace_id: workspaceId,
          project_id: projectId,
        }),
      });

      statusText = `Fetching… (run ${res.run_id.slice(0, 8)})`;
      pollTimer = setInterval(() => void pollProgress(res.run_id), 2000);
    } catch (e) {
      error = e instanceof Error ? e.message : 'Fetch failed.';
      fetching = false;
      statusText = null;
    }
  }
</script>

<div class="step">
  <h2 class="step-title">Select project</h2>
  <p class="step-hint">Connected as <strong>{displayName}</strong>. Enter the project or team identifier to import.</p>

  <div class="form">
    <label class="field">
      <span class="field-label">{sourceLabel[source]}</span>
      <input
        class="field-input"
        type="text"
        placeholder={sourcePlaceholder[source]}
        bind:value={sourceProjectId}
        disabled={fetching}
      />
    </label>

    {#if statusText}
      <p class="status">{statusText}</p>
    {/if}

    {#if error}
      <p class="error">{error}</p>
    {/if}

    <div class="actions">
      <button class="btn-secondary" onclick={onBack} disabled={fetching}>Back</button>
      <button class="btn-primary" onclick={fetchData} disabled={fetching || !sourceProjectId.trim()}>
        {#if fetching}
          <span class="spinner" aria-hidden="true"></span> Fetching…
        {:else}
          Fetch Data
        {/if}
      </button>
    </div>
  </div>
</div>

<style>
.step { display: flex; flex-direction: column; gap: 1rem; }
.step-title { margin: 0; font-size: 1.05rem; font-weight: 600; color: var(--text-primary); }
.step-hint { margin: 0; font-size: 0.82rem; color: var(--text-muted); }
.step-hint strong { color: var(--text-secondary); font-weight: 600; }

.form { display: flex; flex-direction: column; gap: 0.875rem; margin-top: 0.25rem; }

.field { display: flex; flex-direction: column; gap: 0.35rem; }
.field-label { font-size: 0.78rem; font-weight: 500; color: var(--text-secondary); }
.field-input {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 0.85rem;
  padding: 0.5rem 0.75rem;
  outline: none;
  transition: border-color 120ms ease;
}
.field-input:focus { border-color: var(--ab-purple-core); }
.field-input:disabled { opacity: 0.5; cursor: not-allowed; }

.status { margin: 0; font-size: 0.78rem; color: var(--text-secondary); }
.error { margin: 0; font-size: 0.78rem; color: var(--ab-red-bright); }

.actions { display: flex; gap: 0.5rem; justify-content: flex-end; margin-top: 0.25rem; }

.btn-primary {
  display: flex; align-items: center; gap: 0.4rem;
  background: oklch(0.72 0.28 310 / 0.12);
  border: 1px solid oklch(0.72 0.28 310 / 0.32);
  color: var(--ab-purple-bright);
  padding: 0.4rem 1.25rem;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.82rem;
  transition: background 120ms ease;
}
.btn-primary:hover:not(:disabled) { background: oklch(0.72 0.28 310 / 0.22); }
.btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }

.btn-secondary {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  color: var(--text-muted);
  padding: 0.4rem 0.875rem;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.82rem;
  transition: background 120ms ease;
}
.btn-secondary:hover:not(:disabled) { background: rgba(255,255,255,0.07); }
.btn-secondary:disabled { opacity: 0.4; cursor: not-allowed; }

.spinner {
  display: inline-block;
  width: 12px; height: 12px;
  border: 2px solid rgba(255,255,255,0.2);
  border-top-color: var(--ab-purple-bright);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
